/**
 * This hook is for combining payment transactions and Fx orders.
 * We are fetching all payment data and Fx transactions first.
 * Then we sort the list by date.
 * After combining and sorting the list, the user can filter this list by selected date range in UI.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { MARKET_DATA_DATE, MARKET_DATA_TIME_AND_DATE_WITH_TZ2 } from '../../../constants/date-time.constants';
import { ClientSidePagedResponse } from '../../../models/common-response-types';
import { DEFAULT_STATUS_PAGE_RESPONSE } from '../../../models/pageable';
import { PaymentAndFxOrder, ReportingCall } from '../../../models/reporting/types';
import { RootState } from '../../../store';
import { getPayments } from '../../../store/payment';
import {
  ALL_FX_TRANSACTIONS_FILTER,
  ALL_TRANSACTIONS_FILTER,
  DEFAULT_TRANSACTIONS_PAGE_SIZE,
} from '../../../store/payment/constants';
import { PaymentCall } from '../../../store/payment/types';
import { getFxTransactions } from '../../../store/reporting';
import { ORDERS_REQUEST_PAGE_NUMBER } from '../../../store/reporting/constants';
import { isCallStatusError, isCallStatusPending, isCallStatusReady } from '../../error-handling/StatusByCallHelpers';
import { DateRangeType, NullableString } from '../../types';

import { getFxOrderDateByStatus, isSellOrBuyEUR } from './helpers';

type ClientSidePagedResponseType = ClientSidePagedResponse<PaymentAndFxOrder> & {
  resetFxTransactions: () => void;
};

export const usePaymentFxOrders = (): ClientSidePagedResponseType => {
  const [ page, setPage ] = useState(ORDERS_REQUEST_PAGE_NUMBER);
  const [ dateRange, setDateRange ] = useState<DateRangeType>({
    startDate: '',
    endDate: '',
  });
  const paymentTransactions = useSelector((state: RootState) => state.payment.paymentsAll.data);

  let accountInfo: any = useSelector((state: RootState) => state.crm.accountInfo);
  if (isEmpty(accountInfo)) { // TODO: this check will be removed after the ART-4346 merge
    accountInfo = [];
  }

  const accounts = accountInfo.map((item: any) => item.account_reference_id);

  const fxTransactions = useSelector((state: RootState) => state.reporting.fxTransactions);
  const isPaymentsReady: boolean = useSelector(
    ({ payment }: RootState) => isCallStatusReady(PaymentCall.getPaymentsAll, payment.statusByCall),
  );
  const isPaymentLoading: boolean = useSelector(
    ({ payment }: RootState) => isCallStatusPending(PaymentCall.getPaymentsAll, payment.statusByCall),
  );
  const hasPaymentError: boolean = useSelector(
    ({ payment }: RootState) => isCallStatusError(PaymentCall.getPaymentsAll, payment.statusByCall),
  );
  const isFxReady: boolean = useSelector(
    ({ reporting }: RootState) => isCallStatusReady(ReportingCall.getFxTransactions, reporting.statusByCall),
  );
  const isFxLoading: boolean = useSelector(
    ({ reporting }: RootState) => isCallStatusPending(ReportingCall.getFxTransactions, reporting.statusByCall),
  );

  const hasFxError: boolean = useSelector(
    ({ reporting }: RootState) => isCallStatusError(ReportingCall.getFxTransactions, reporting.statusByCall),
  );

  const filterContentBy = (
    pageNumber: number,
    startDateStr?: NullableString,
    endDateStr?: NullableString,
  ): void => {
    const endIndex = DEFAULT_TRANSACTIONS_PAGE_SIZE * pageNumber;
    const currentTransactions = result.content.slice(0, endIndex);

    if (startDateStr) {
      const resultByFilter: PaymentAndFxOrder[] = [];
      const startDateTimestamp = moment(startDateStr, MARKET_DATA_DATE)
        .utc()
        .valueOf();
      const endDateTimestamp = moment(endDateStr, MARKET_DATA_DATE)
        .utc()
        .add(1, 'days')
        .valueOf();

      for (const item of result.content) {
        if (item.timestamp <= endDateTimestamp
          && item.timestamp >= startDateTimestamp
        ) {
          resultByFilter.push(item);
        }
      }

      result.filteredContent = resultByFilter.length > endIndex ? resultByFilter.slice(0, endIndex) : resultByFilter;
    } else {
      result.filteredContent = currentTransactions;
    }
  };

  const changePage = (pageNumber: number): void => {
    filterContentBy(pageNumber);
    setPage(pageNumber);
  };

  const changeDate = (range: DateRangeType): void => {
    filterContentBy(page, range.startDate, range.endDate);
    setPage(ORDERS_REQUEST_PAGE_NUMBER);
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const resetFxTransactions = (): void => {
    if (accounts.length > 0) {
      dispatch(getFxTransactions({
        ...ALL_FX_TRANSACTIONS_FILTER,
        accountsList: accounts,
      }));
    }
  };

  const result = {
    ...DEFAULT_STATUS_PAGE_RESPONSE,
    dateRange,
    setPage: changePage,
    setDateRange: changeDate,
    ...{ number: page },
    resetFxTransactions,
  } as ClientSidePagedResponseType;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isFxLoading || isFxReady || hasFxError) {
      return;
    }
    resetFxTransactions();
  }, [ isFxReady, hasFxError, isFxLoading, accounts, fxTransactions ]);

  useEffect(() => {
    if (isPaymentLoading || isPaymentsReady || hasPaymentError) {
      return;
    }
    if (accounts.length > 0) {
      dispatch(getPayments(ALL_TRANSACTIONS_FILTER));
    }
  }, [ isPaymentsReady, hasPaymentError, isPaymentLoading, paymentTransactions ]);

  if (isFxLoading || isPaymentLoading) {
    return result;
  }

  const paymentAndFxOrderList: PaymentAndFxOrder[] = [];
  fxTransactions.accounts.forEach(account => {
    account.fxOrders?.forEach(item => {
      if (isSellOrBuyEUR(item)) {
        paymentAndFxOrderList.push({
          isPayment: false,
          timestamp: moment(getFxOrderDateByStatus(item), MARKET_DATA_TIME_AND_DATE_WITH_TZ2)
            .valueOf(),
          orderOrPayment: item,
        });
      }
    });
  });

  paymentTransactions?.forEach(item => {
    paymentAndFxOrderList.push({
      isPayment: true,
      timestamp: moment(item.create_date, MARKET_DATA_TIME_AND_DATE_WITH_TZ2)
        .valueOf(),
      orderOrPayment: item,
    });
  });

  result.content = paymentAndFxOrderList.sort(
    (firstItem, secondItem) => firstItem.timestamp - secondItem.timestamp,
  )
    .reverse();

  filterContentBy(page, dateRange.startDate, dateRange.endDate);
  result.totalElements = result.content.length;
  result.numberOfElements = result.filteredContent.length;
  result.hasMorePages = !isEmpty(result.content)
    && result.totalElements > result.numberOfElements
    && result.filteredContent.length >= DEFAULT_TRANSACTIONS_PAGE_SIZE;

  return { ...result, resetFxTransactions };
};

